import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtTime } from "@/lib/api";
import { PageHeader } from "@/components/bits";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Building2, UserPlus, Trash2, AlertTriangle } from "lucide-react";

export default function Tenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [name, setName] = useState("");
  const [uEmail, setUEmail] = useState("");
  const [uName, setUName] = useState("");
  const [uPassword, setUPassword] = useState("");
  const [uTenant, setUTenant] = useState("");
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delConfirm, setDelConfirm] = useState("");

  const load = () => api.get("/tenants").then((r) => setTenants(r.data)).catch((e) => toast.error(fmtErr(e)));

  useEffect(() => {
    load();
  }, []);

  async function createTenant(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tenants", { name: name.trim() });
      toast.success("Mandant angelegt");
      setName("");
      load();
    } catch (e2) {
      toast.error(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  }

  async function deleteTenant() {
    setBusy(true);
    try {
      const { data } = await api.delete(`/tenants/${delTarget.id}`, { params: { confirm: delConfirm } });
      const c = data.deleted || {};
      toast.success(`Mandant „${data.name}" gelöscht — ${c.users || 0} Benutzer, ${c.devices || 0} Geräte, ${c.playlists || 0} Playlists, ${c.media || 0} Medien entfernt`);
      setDelTarget(null);
      setDelConfirm("");
      load();
    } catch (e2) {
      toast.error(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  }

  async function createUser(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", {
        email: uEmail.trim(),
        name: uName.trim(),
        password: uPassword,
        role: "TENANT_ADMIN",
        tenant_id: uTenant,
      });
      toast.success(`Tenant-Admin angelegt: ${uEmail} — muss beim ersten Login ein neues Passwort setzen`);
      setUEmail("");
      setUName("");
      setUPassword("");
      load();
    } catch (e2) {
      toast.error(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="tenants-page">
      <PageHeader title="Mandanten" sub="Tenant-Verwaltung (nur SUPER_ADMIN) — Klick auf einen Mandanten öffnet die Kunden-Akte (Geräte, Benutzer, Tickets)" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <form onSubmit={createTenant} className="panel p-5 space-y-3" data-testid="tenant-create-form">
          <div className="mono-label flex items-center gap-2"><Building2 size={13} /> Neuer Mandant</div>
          <div className="flex gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-dark flex-1"
              placeholder="Firmenname"
              required
              data-testid="tenant-name-input"
            />
            <button type="submit" disabled={busy} className="btn-primary" data-testid="tenant-create-button">
              Anlegen
            </button>
          </div>
        </form>

        <form onSubmit={createUser} className="panel p-5 space-y-3" data-testid="user-create-form">
          <div className="mono-label flex items-center gap-2"><UserPlus size={13} /> Neuer Tenant-Admin</div>
          <div className="grid grid-cols-2 gap-2">
            <input value={uEmail} onChange={(e) => setUEmail(e.target.value)} type="email" className="input-dark" placeholder="E-Mail" required data-testid="user-email-input" />
            <input value={uName} onChange={(e) => setUName(e.target.value)} className="input-dark" placeholder="Name" required data-testid="user-name-input" />
            <input value={uPassword} onChange={(e) => setUPassword(e.target.value)} type="password" className="input-dark" placeholder="Passwort" required minLength={8} data-testid="user-password-input" />
            <select value={uTenant} onChange={(e) => setUTenant(e.target.value)} className="input-dark" required data-testid="user-tenant-select">
              <option value="">— Mandant —</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="user-create-button">
            Tenant-Admin anlegen
          </button>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Nur SUPER_ADMIN darf Zugänge anlegen. Der Kunde erhält die Zugangsdaten von dir und muss beim
            ersten Login ein eigenes Passwort vergeben — alle API-Endpunkte sind bis dahin gesperrt.
          </p>
        </form>
      </div>

      <div className="panel" data-testid="tenants-table-panel">
        <table className="w-full text-sm" data-testid="tenants-table">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="mono-label font-normal px-5 py-3">Mandant</th>
              <th className="mono-label font-normal px-3 py-3">Benutzer</th>
              <th className="mono-label font-normal px-3 py-3">Geräte</th>
              <th className="mono-label font-normal px-3 py-3">Status</th>
              <th className="mono-label font-normal px-3 py-3">Angelegt</th>
              <th className="mono-label font-normal px-3 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr
                key={t.id}
                onClick={() => navigate(`/tenants/${t.id}`)}
                className="border-b border-border/50 cursor-pointer hover:bg-slate-800/40 transition-colors"
                data-testid={`tenant-row-${t.id}`}
              >
                <td className="px-5 py-3 text-slate-200 font-medium">{t.name}</td>
                <td className="px-3 py-3 font-mono text-xs text-slate-400">{t.user_count}</td>
                <td className="px-3 py-3 font-mono text-xs text-slate-400">{t.device_count}</td>
                <td className="px-3 py-3">
                  {t.subscription_status === "CANCELLED" ? (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-red-400 border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 rounded-sm">
                      Gekündigt
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 rounded-sm">
                      Aktiv
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 font-mono text-xs text-slate-300">{fmtTime(t.created_at)}</td>
                <td className="px-3 py-3 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); setDelTarget(t); setDelConfirm(""); }}
                    className="p-1.5 rounded-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title={`Mandant „${t.name}" löschen`}
                    data-testid={`tenant-delete-${t.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) { setDelTarget(null); setDelConfirm(""); } }}>
        <DialogContent className="bg-card border-border" data-testid="tenant-delete-dialog">
          <DialogTitle className="font-display flex items-center gap-2 text-red-400">
            <AlertTriangle size={16} /> Mandant endgültig löschen
          </DialogTitle>
          {delTarget && (
            <div className="space-y-4">
              <p className="text-xs text-slate-300 leading-relaxed">
                <span className="font-semibold text-slate-100">„{delTarget.name}"</span> wird mit allen Daten
                gelöscht: {delTarget.user_count} Benutzer, {delTarget.device_count} Geräte, dazu Playlists,
                Medien, Zeitpläne, Gruppen und Tickets.
              </p>
              <p className="text-xs text-amber-300/90 leading-relaxed border border-amber-500/30 bg-amber-500/5 rounded-sm px-3 py-2">
                Gekoppelte Geräte verlieren ihre Zugangsdaten — die Cloud antwortet ihnen danach mit 401 und
                sie setzen sich nach wenigen Minuten selbst zurück (Onboarding-Bildschirm).
              </p>
              <div>
                <label className="mono-label block mb-1.5">
                  Zur Bestätigung den Namen eintippen: <span className="text-red-400">{delTarget.name}</span>
                </label>
                <input
                  value={delConfirm}
                  onChange={(e) => setDelConfirm(e.target.value)}
                  className="input-dark w-full"
                  placeholder={delTarget.name}
                  autoFocus
                  data-testid="tenant-delete-confirm-input"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setDelTarget(null); setDelConfirm(""); }} className="btn-ghost flex-1 text-xs" data-testid="tenant-delete-cancel">
                  Abbrechen
                </button>
                <button
                  onClick={deleteTenant}
                  disabled={busy || delConfirm !== delTarget.name}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-sm bg-red-500 px-3 py-2 text-xs font-semibold text-white hover:bg-red-400 disabled:opacity-40 transition-colors"
                  data-testid="tenant-delete-confirm"
                >
                  <Trash2 size={13} /> Endgültig löschen
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
